import externalLinkIcon from 'assets/icons/external-link.svg'
import { Modal } from 'components/Modal'
import { DetailBand, getBandedWorkstreamDetailModalData } from 'constants/WorkingModePathwaysDetailModals'

import { DetailSubsectionBlock, StepsList } from './DetailModalParts'
import { ModalHeader } from './ModalHeader'

interface BandedWorkstreamDetailModalProps {
  isOpen: boolean
  onClose: () => void
  id: string
}

const Band = ({ band }: { band: DetailBand }) => {
  const hasFooter = band.subsections.some(subsection => subsection.note || subsection.showDownloadButton)

  return (
    <div
      className={`banded-detail-band${hasFooter ? ' banded-detail-band--stretch' : ''}`}
      style={{ background: band.color }}
    >
      <p className="title-xs w-full">{band.heading}</p>
      <div className="flex gap-4 items-stretch w-full flex-1">
        {band.subsections.flatMap((subsection, index) => [
          ...(index > 0 ? [<div key={`divider-${subsection.label}`} className="banded-detail-divider" />] : []),
          <div key={subsection.label} className="flex-1 min-w-0">
            <DetailSubsectionBlock subsection={subsection} />
          </div>,
        ])}
      </div>
    </div>
  )
}

const OutputPane = ({
  thumbnails,
  outputColor,
  muralUrl,
}: {
  thumbnails?: { label: string; imgUrl?: string }[]
  outputColor?: string
  muralUrl?: string
}) => (
  <div
    className={`banded-detail-output${outputColor ? '' : ' banded-detail-output--bordered'}`}
    style={outputColor ? { background: outputColor } : undefined}
  >
    <p className="title-xs w-full">Output</p>
    {thumbnails && (
      <div className="banded-detail-thumbnails">
        {thumbnails.map(thumbnail => (
          <div key={thumbnail.label} className="banded-detail-thumbnail">
            <p className="subtitle-xs banded-detail-thumbnail-label">{thumbnail.label}</p>
            <img src={thumbnail.imgUrl || ''} alt="" className="banded-detail-thumbnail-image" />
          </div>
        ))}
      </div>
    )}
    <div className="text-right">
      <a
        href={muralUrl || '#'}
        target="_blank"
        rel="noreferrer"
        className="btn-black border border-solid body-s font-bold px-[16px] py-[6px] inline-flex items-center gap-2 w-[96px]"
      >
        Mural
        <img src={externalLinkIcon} alt="" width={16} height={16} />
      </a>
    </div>
  </div>
)

export const BandedWorkstreamDetailModal = ({ isOpen, onClose, id }: BandedWorkstreamDetailModalProps) => {
  const data = getBandedWorkstreamDetailModalData(id)
  const isSprintWorkstream = id === 'e2e-day4-detail'

  if (!data) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={isSprintWorkstream ? 'banded-detail-modal banded-detail-modal--sprint' : 'banded-detail-modal'}
    >
      <ModalHeader title={data.title} onClose={onClose} />

      <div className="flex gap-4 items-start w-full px-6 pb-6">
        <div className="banded-detail-band-container">
          {data.layout === 'right-rail' ? (
            data.bands.map(band => <Band key={band.heading} band={band} />)
          ) : (
            <>
              <Band band={data.bands[0]} />
              <div className="banded-detail-split-row">
                <Band band={data.bands[1]} />
                <OutputPane
                  thumbnails={data.outputThumbnails}
                  outputColor={data.outputbgColor}
                  muralUrl={data.muralUrl}
                />
              </div>
            </>
          )}
        </div>

        {data.layout === 'right-rail' && (
          <div className="banded-detail-rail">
            <OutputPane thumbnails={data.outputThumbnails} outputColor={data.outputbgColor} muralUrl={data.muralUrl} />
          </div>
        )}

        {data.steps && (
          <div className="banded-detail-steps-column">
            <StepsList steps={data.steps} />
          </div>
        )}
      </div>
    </Modal>
  )
}
